import { ReactNode, useState, useCallback } from 'react';
import {
    useIsActive,
    useIsOver,
    useDragAndDropState,
    useDragAndDropStateSetters,
} from '../../../../stores/mappingStore';
import { MappingsDraggedItemType, StrategyType } from '../../../../types';
import { STRATEGY } from '../../constants';
import { DragAndDropZone, Draggable, Droppable } from './components/dragAndDrop/dragAndDrop';
import './styles.css';

type BaseMappingsSortableItemProps<T extends StrategyType[]> = {
    id: string;
    overId: string;
    children: ReactNode;
    draggingDisabled?: boolean;
    droppingDisabled?: boolean;
    itemType?: MappingsDraggedItemType | null;
    strategies: T;
};

// Conditional type to require handleOnDropInsert if "STRATEGY.INSERT" is in strategies
type RequireInsert<T extends StrategyType[]> = typeof STRATEGY.INSERT extends T[number]
    ? { handleOnDropInsert: () => void }
    : { handleOnDropInsert?: () => void };
// Conditional type to require handleOnDropInsert if "STRATEGY.SWAP" is in strategies
type RequireSwap<T extends StrategyType[]> = typeof STRATEGY.SWAP extends T[number]
    ? { handleOnDropSwap: () => void }
    : { handleOnDropSwap?: () => void };

type MappingsSortableItemPropsType<T extends StrategyType[]> = BaseMappingsSortableItemProps<T> &
    RequireInsert<T> &
    RequireSwap<T>;

function MappingsSortableItem<T extends StrategyType[]>({
    handleOnDropSwap,
    handleOnDropInsert,
    children,
    draggingDisabled = false,
    droppingDisabled = false,
    itemType = null,
    strategies,
    id,
    overId,
}: MappingsSortableItemPropsType<T>) {
    const [selectedStrategy, setSelectedStrategy] = useState<StrategyType | null>(null);
    const isDragging = useIsActive(id);
    const isOver = useIsOver(overId);
    const { activeId } = useDragAndDropState();
    const { setActiveId, setOverId, setDraggedItemType } = useDragAndDropStateSetters();

    const handleDragStart = useCallback(() => {
        setActiveId(id);
        setDraggedItemType(itemType);
    }, [setActiveId, id]);

    const handleDragOver = useCallback((e: React.DragEvent) => {
        e.preventDefault();
    }, []);

    const handleDragEnter = useCallback(() => {
        if (!activeId) {
            return;
        }
        setOverId(overId);
    }, [setOverId, activeId, overId]);

    const handleDragEnd = useCallback(() => {
        setActiveId(undefined);
        setOverId(undefined);
        setDraggedItemType(null);
    }, [setActiveId, setOverId]);

    const showDropOptions = isOver && !isDragging && !droppingDisabled;

    return (
        <DragAndDropZone handleDragEnter={handleDragEnter} className={`dnd__container ${isOver ? 'over' : ''}`}>
            <Draggable
                className={`full-height ${showDropOptions ? 'pb-40' : ''}`}
                handleDragEnd={handleDragEnd}
                handleDragStart={() => {
                    if (draggingDisabled) {
                        return;
                    }
                    handleDragStart();
                }}
            >
                {children}
            </Draggable>
            {showDropOptions && (
                <div className="dnd__strategy__overlay">
                    {strategies.includes(STRATEGY.INSERT) && (
                        <Droppable
                            className={`dnd__strategy__wrapper`}
                            handleDragEnter={() => {
                                setSelectedStrategy(STRATEGY.INSERT);
                            }}
                            handleDragOver={handleDragOver}
                            handleDrop={handleOnDropInsert!}
                        >
                            <div
                                className={`dnd__strategy insert ${selectedStrategy === STRATEGY.INSERT ? 'over' : ''}`}
                            >
                                INSERT
                            </div>
                        </Droppable>
                    )}
                    {strategies.includes(STRATEGY.SWAP) && (
                        <Droppable
                            className={`dnd__strategy__wrapper`}
                            handleDragEnter={() => {
                                setSelectedStrategy(STRATEGY.SWAP);
                            }}
                            handleDragOver={handleDragOver}
                            handleDrop={handleOnDropSwap!}
                        >
                            <div className={`dnd__strategy swap ${selectedStrategy === STRATEGY.SWAP ? 'over' : ''}`}>
                                SWAP
                            </div>
                        </Droppable>
                    )}
                </div>
            )}
        </DragAndDropZone>
    );
}

export default MappingsSortableItem;
